import '../styles/LoginAccessPanel.css';
import React, { useRef, useState } from 'react';

const COPY = {
  enroll: {
    kicker: 'Identity access · first use',
    heading: 'Enroll to start extracting.',
    lede: 'Your lead lists stay private to you and remain available when you return in a later session.',
    submit: 'Enroll identity',
    submitting: 'Enrolling identity',
    retry: 'Retry enrollment',
    intro: 'Choose an email and a password for your researcher identity.',
    success: 'Identity enrolled',
  },
  signin: {
    kicker: 'Identity access · returning researcher',
    heading: 'Sign in to your lead lists.',
    lede: 'Verify your identity to reach your own saved lead lists and run a new extraction.',
    submit: 'Sign in',
    submitting: 'Verifying identity',
    retry: 'Retry sign in',
    intro: 'Enter the credentials you enrolled with.',
    success: 'Identity verified',
  },
};

const MODES = [
  { id: 'enroll', index: '01', label: 'Enroll' },
  { id: 'signin', index: '02', label: 'Sign in' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(mode, email, password) {
  const fieldErrors = {};
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    fieldErrors.email = 'Enter the email for your researcher identity.';
  } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
    fieldErrors.email = 'This email is incomplete. Use the form name@domain.com.';
  }
  if (!password) {
    fieldErrors.password = 'Enter your password.';
  } else if (mode === 'enroll' && password.length < 8) {
    fieldErrors.password = 'Use at least 8 characters for the password.';
  }
  return fieldErrors;
}

function LoginAccessPanel({ onSubmit } = {}) {
  const [mode, setMode] = useState('enroll');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const formRef = useRef(null);

  const copy = COPY[mode];

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setFieldErrors({});
    setFormError('');
    setSubmitResult(null);
  };

  const onTabKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next = mode === 'enroll' ? 'signin' : 'enroll';
    switchMode(next);
    const target = formRef.current && formRef.current.querySelector(`[data-mode-tab="${next}"]`);
    if (target) target.focus();
  };

  const submit = async () => {
    if (submitting) return;
    const errors = validate(mode, email, password);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError('');
      setSubmitResult(null);
      const firstRef = errors.email ? emailRef : passwordRef;
      if (firstRef.current) firstRef.current.focus();
      return;
    }
    setFieldErrors({});
    setFormError('');
    setSubmitResult(null);

    if (typeof onSubmit !== 'function') {
      setFormError(
        mode === 'enroll'
          ? 'Enrollment is not available in this preview. No identity was created.'
          : 'Sign in is not available in this preview. No identity was verified.'
      );
      return;
    }

    setSubmitting(true);
    let outcome = null;
    let thrown = null;
    try {
      outcome = await onSubmit({ mode, email: email.trim(), password });
    } catch (error) {
      thrown = error;
    }
    setSubmitting(false);

    const normalized =
      outcome && typeof outcome === 'object'
        ? outcome
        : outcome === true
          ? { ok: true }
          : outcome === false
            ? { ok: false }
            : null;

    if (thrown || !normalized) {
      setFormError(
        (thrown && thrown.message) ||
          (normalized && normalized.message) ||
          (mode === 'enroll'
            ? 'Enrollment did not complete. Check your details and retry.'
            : 'Verification did not complete. Check your details and retry.')
      );
      return;
    }

    if (normalized.ok === false) {
      setFormError(
        normalized.message ||
          (mode === 'enroll'
            ? 'Enrollment did not complete. Check your details and retry.'
            : 'That email and password did not verify. Check them and retry, or enroll if you have no identity yet.')
      );
      return;
    }

    if (normalized.fieldErrors && typeof normalized.fieldErrors === 'object') {
      setFieldErrors(normalized.fieldErrors);
    }
    setSubmitResult({
      mode,
      email: normalized.email || email.trim(),
      identity: normalized.identity || normalized.researcherId || normalized.id || '',
    });
  };

  const onFormSubmit = (event) => {
    event.preventDefault();
    submit();
  };

  const handleRetry = () => {
    setFormError('');
    // Keep entered values; re-run the same submission.
    submit();
  };

  const fieldError = (key) => fieldErrors && fieldErrors[key];

  return (
    <section className="lap-root" aria-labelledby="lap-heading">
      <div className="lap-panel">
        <header className="lap-head">
          <p className="lap-kicker">
            <span className="lap-kicker-dot" aria-hidden="true" />
            {copy.kicker}
          </p>
          <h1 className="lap-heading" id="lap-heading">
            {copy.heading}
          </h1>
          <p className="lap-lede">{copy.lede}</p>
        </header>

        <div className="lap-switch" role="tablist" aria-label="Identity access mode" data-mode={mode}>
          {MODES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`lap-tab-${item.id}`}
              aria-selected={mode === item.id}
              aria-controls="lap-panel-form"
              tabIndex={mode === item.id ? 0 : -1}
              data-mode-tab={item.id}
              className="lap-switch-tab"
              onClick={() => switchMode(item.id)}
              onKeyDown={onTabKeyDown}
              disabled={submitting}
            >
              <span className="lap-switch-index" aria-hidden="true">{item.index}</span>
              <span className="lap-switch-dot" aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          ))}
          <span className="lap-switch-marker" aria-hidden="true" />
        </div>

        <form
          className="lap-form"
          id="lap-panel-form"
          ref={formRef}
          role="tabpanel"
          aria-labelledby={`lap-tab-${mode}`}
          onSubmit={onFormSubmit}
          noValidate
        >
          <div className="lap-form-intro">
            <p className="lap-form-intro-text">{copy.intro}</p>
          </div>

          <div className="lap-field" data-invalid={fieldError('email') ? 'true' : 'false'}>
            <div className="lap-field-head">
              <label className="lap-label" htmlFor="lap-email">Email</label>
              <span className="lap-field-meta">Researcher identity</span>
            </div>
            <input
              ref={emailRef}
              id="lap-email"
              className="lap-input"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              spellCheck="false"
              value={email}
              disabled={submitting}
              aria-invalid={fieldError('email') ? 'true' : 'false'}
              aria-describedby={fieldError('email') ? 'lap-email-error' : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
                if (fieldError('email')) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.email;
                    return next;
                  });
                }
              }}
            />
            <div className="lap-field-rule" aria-hidden="true" />
            {fieldError('email') ? (
              <p className="lap-error" id="lap-email-error">
                <span className="lap-error-mark" aria-hidden="true" />
                <span>{fieldError('email')}</span>
              </p>
            ) : null}
          </div>

          <div className="lap-field" data-invalid={fieldError('password') ? 'true' : 'false'}>
            <div className="lap-field-head">
              <label className="lap-label" htmlFor="lap-password">Password</label>
              <span className="lap-field-meta">
                {mode === 'enroll' ? 'Minimum 8 characters' : 'Stored credential'}
              </span>
            </div>
            <input
              ref={passwordRef}
              id="lap-password"
              className="lap-input"
              type="password"
              name="password"
              autoComplete={mode === 'enroll' ? 'new-password' : 'current-password'}
              value={password}
              disabled={submitting}
              aria-invalid={fieldError('password') ? 'true' : 'false'}
              aria-describedby={fieldError('password') ? 'lap-password-error' : undefined}
              onChange={(event) => {
                setPassword(event.target.value);
                if (fieldError('password')) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.password;
                    return next;
                  });
                }
              }}
            />
            <div className="lap-field-rule" aria-hidden="true" />
            {fieldError('password') ? (
              <p className="lap-error" id="lap-password-error">
                <span className="lap-error-mark" aria-hidden="true" />
                <span>{fieldError('password')}</span>
              </p>
            ) : null}
          </div>

          <div className="lap-form-error" role="alert" aria-live="polite" hidden={!formError}>
            <div className="lap-form-error-head">
              <span className="lap-form-error-dot" aria-hidden="true" />
              <p className="lap-form-error-title">
                {mode === 'enroll' ? 'Enrollment failed' : 'Verification failed'}
              </p>
            </div>
            <p className="lap-form-error-text">{formError || ' '}</p>
            <button
              type="button"
              className="lap-retry"
              onClick={handleRetry}
              disabled={submitting}
            >
              {copy.retry}
            </button>
          </div>

          <div className="lap-actions">
            <button
              type="submit"
              className="lap-submit"
              data-submitting={submitting ? 'true' : 'false'}
              disabled={submitting}
            >
              {submitting ? (
                <span className="lap-submit-progress" aria-hidden="true" />
              ) : null}
              <span>{submitting ? copy.submitting : copy.submit}</span>
            </button>
            {mode === 'signin' ? (
              <p className="lap-footnote">
                No identity yet?{' '}
                <button
                  type="button"
                  className="lap-footnote-link"
                  onClick={() => switchMode('enroll')}
                  disabled={submitting}
                >
                  Switch to Enroll
                </button>
              </p>
            ) : null}
          </div>
        </form>

        <div className="lap-success" aria-live="polite" hidden={!submitResult}>
          <div className="lap-success-head">
            <span className="lap-kicker-dot" aria-hidden="true" />
            <p className="lap-success-title">{submitResult ? COPY[submitResult.mode].success : ''}</p>
          </div>
          <dl className="lap-success-id">
            <div className="lap-success-row">
              <dt className="lap-success-label">Email</dt>
              <dd className="lap-success-value" data-mono="true">{submitResult ? submitResult.email : ''}</dd>
            </div>
            {submitResult && submitResult.identity ? (
              <div className="lap-success-row">
                <dt className="lap-success-label">Identity</dt>
                <dd className="lap-success-value" data-mono="true">{submitResult.identity}</dd>
              </div>
            ) : null}
            <div className="lap-success-row">
              <dt className="lap-success-label">Next</dt>
              <dd className="lap-success-value">Extraction</dd>
            </div>
          </dl>
          <a className="lap-success-go" href="/extraction">
            <span>Continue to Extraction</span>
            <span className="lap-success-arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default LoginAccessPanel;